import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Alert, Box, Button, Modal, Select, Stack, Text } from "@mantine/core";
import ConfirmationModal from "../../../maintenance/components/platformDetail/ConfirmationModal";
import PlatformStepper from "./PlatformStepper";
import { AssignableUnit, Platform, PlatformUnit, PlatformUnitType } from "./types";
import {
  MAINTENANCE_STEPS,
  maintenanceStepIndex,
  mapPlatformStatus,
} from "./status";
import { useIsNarrow } from "../useIsNarrow";
import { PlatformStatusPill } from "./PlatformStatusPill";

export type PlatformActionKind =
  | "request"
  | "return"
  | "reset"
  | "save"
  | "moveToMaintenance"
  | "returnFromMaintenance";
export interface SavePlatformPayload {
  platformUnitTypeId: number | null;
  units: { unitId: number; tValue: string | null }[];
}

export interface PlatformPending {
  action: PlatformActionKind;
  unitId?: number;
}

interface PlatformModalProps {
  opened: boolean;
  onClose: () => void;
  platform: Platform;
  unitTypes: PlatformUnitType[];
  assignableUnits: AssignableUnit[];
  assignableUnitsLoading?: boolean;
  assignableUnitsError?: boolean;
  assignUnits?: boolean;
  tValue?: boolean;
  platformSide?: boolean;
  onRequest: (unit: PlatformUnit) => void;
  onReturn: (unit: PlatformUnit) => void;
  onReset: () => void;
  pending: PlatformPending | null;
  errorText?: string | null;
  onDismissError?: () => void;
  onMoveToMaintenance?: () => void;
  onSave?: (payload: SavePlatformPayload) => void;
  onReturnFromMaintenance?: () => void;
}

interface Slot {
  unitId: string | null;
  tValue: string | null;
}

const DROPDOWN_PROPS = {
  withinPortal: true,
  zIndex: 1200,
  shadow: "md",
  offset: 4,
} as const;

const c = {
  slate400: "#94a3b8",
  slate500: "#64748b",
  slate900: "#0f172a",
  border: "#e2e8f0",
  slate50: "#f8fafc",
};

const miniLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: c.slate400,
};

function SectionLabel({ children, mb = 8 }: { children: ReactNode; mb?: number }) {
  return (
    <Text style={{ ...miniLabel, display: "block", marginBottom: mb }}>{children}</Text>
  );
}

function slotsFor(platform: Platform, unitTypes: PlatformUnitType[]): Slot[] {
  const size = unitTypes.find((t) => t.id === platform.platformUnitTypeId)?.numberOfUnits ?? 0;
  const assigned: Slot[] = platform.units.map((u) => ({
    unitId: String(u.unitId),
    tValue: u.tValue,
  }));
  return resize(assigned, Math.max(size, assigned.length));
}

function resize(slots: Slot[], size: number): Slot[] {
  if (slots.length >= size) return slots.slice(0, size);
  return [
    ...slots,
    ...Array.from({ length: size - slots.length }, () => ({
      unitId: null,
      tValue: null,
    })),
  ];
}

const slotsKey = (slots: Slot[]) =>
  slots
    .filter((s) => s.unitId)
    .map((s) => `${s.unitId}:${s.tValue ?? ""}`)
    .sort()
    .join("|");

const assignmentKey = (platform: Platform) =>
  platform.units
    .map((u) => `${u.unitId}:${u.tValue ?? ""}`)
    .sort()
    .join("|");

// While a platform is busy the modal shows a heading + subtext describing what
// it is doing. `busyPhase` collapses the raw/mapped status into one of these
// phases and BUSY_PHASE_COPY holds the wording — one place to read and edit it.
type BusyPhase =
  | "fault"
  | "inMaintenance"
  | "movingToMaintenance"
  | "returningFromMaintenance"
  | "returning"
  | "atDock"
  | "waiting";

function busyPhase(rawStatus: string): BusyPhase {
  // Two raw VLC states share the "Maintenance" label but need distinct copy,
  // so match them before falling back to the mapped display label.
  if (rawStatus === "InMaintenance") return "inMaintenance";
  if (rawStatus === "ReturnFromMaintenanceRequested") return "returningFromMaintenance";

  switch (mapPlatformStatus(rawStatus)) {
    case "Fault":
      return "fault";
    case "Maintenance": // remaining: ToMaintenanceRequested / MovingToMaintenance
      return "movingToMaintenance";
    case "Returning":
      return "returning";
    case "At Dock":
      return "atDock";
    default:
      return "waiting";
  }
}

const BUSY_PHASE_COPY: Record<BusyPhase, { heading: string; subtext: string }> = {
  fault: {
    heading: "Platform fault",
    subtext: "The platform reported a fault. Reset the status, or contact support if it persists.",
  },
  inMaintenance: {
    heading: "Platform in maintenance",
    subtext: "The platform is in the maintenance area. Return it to storage when the work is done.",
  },
  movingToMaintenance: {
    heading: "Moving to maintenance…",
    subtext: "The platform has been called to the maintenance area. Please wait for it to arrive.",
  },
  returningFromMaintenance: {
    heading: "Returning from maintenance…",
    subtext: "The platform is on its way back to storage.",
  },
  returning: {
    heading: "Returning unit to storage…",
    subtext: "The unit is on its way back to storage.",
  },
  atDock: {
    heading: "Unit has arrived at the dock",
    subtext: "The platform has reached the dock.",
  },
  waiting: {
    heading: "Waiting for platform arrival…",
    subtext: "The unit has been requested. Please wait for the platform to reach the dock.",
  },
};

export default function PlatformModal({
  opened,
  onClose,
  platform,
  unitTypes,
  assignableUnits,
  assignableUnitsLoading = false,
  assignableUnitsError = false,
  assignUnits = false,
  tValue = false,
  platformSide = false,
  onRequest,
  onReturn,
  onReset,
  pending,
  errorText,
  onDismissError,
  onMoveToMaintenance,
  onSave,
  onReturnFromMaintenance,
}: PlatformModalProps) {
  const narrow = useIsNarrow();
  const activeLabel = mapPlatformStatus(platform.status);
  const isAvailable = activeLabel === "Available";
  const busy = platform.currentUnitId != null || !isAvailable;
  const phase = busyPhase(platform.status);
  const { heading, subtext } = BUSY_PHASE_COPY[phase];

  const isAtDock = phase === "atDock";
  const isMaintenance =
    phase === "inMaintenance" ||
    phase === "movingToMaintenance" ||
    phase === "returningFromMaintenance";

  const canMoveToMaintenance = onMoveToMaintenance != null;
  const canReturnFromMaintenance = onReturnFromMaintenance != null && phase === "inMaintenance";

  const activeUnit = platform.units.find((u) => u.unitId === platform.currentUnitId) ?? null;
  const anyPending = pending !== null;
  const title = `Platform ${platform.platformNumber} Details`;
  const activeUnitNumber = activeUnit?.unitNumber ?? platform.currentUnitNumber ?? "—";
  const hasActiveUnit = activeUnit != null || platform.currentUnitNumber != null;

  const unitTypeOptions = unitTypes.map((t) => ({ value: String(t.id), label: t.typeName }));
  const savePending = pending?.action === "save";
  // Editing against a half-loaded (or failed) unit list would show every slot as
  // empty and invite the admin to re-assign units that are already assigned.
  const unitsReady = !assignableUnitsLoading && !assignableUnitsError;
  const editable = onSave != null && !anyPending && unitsReady;

  const [draftTypeId, setDraftTypeId] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);

  const resetDraft = useCallback(() => {
    setDraftTypeId(platform.platformUnitTypeId != null ? String(platform.platformUnitTypeId) : null);
    setSlots(slotsFor(platform, unitTypes));
  }, [platform, unitTypes]);

  // Reset only when the SAVED state changes — keyed on a value, not on
  // resetDraft's identity. The platforms query repolls every 15s, and taking a
  // dependency on the platform object would wipe an in-progress edit whenever
  // any unrelated field of it (unit status, timestamps) came back changed.
  const savedKey = `${platform.id}|${platform.platformUnitTypeId ?? ""}|${assignmentKey(platform)}`;
  const resetDraftRef = useRef(resetDraft);
  resetDraftRef.current = resetDraft;

  useEffect(() => {
    resetDraftRef.current();
  }, [savedKey]);

  const dirty =
    draftTypeId !== (platform.platformUnitTypeId != null ? String(platform.platformUnitTypeId) : null) ||
    slotsKey(slots) !== slotsKey(slotsFor(platform, unitTypes));

  const changeType = (value: string | null) => {
    setDraftTypeId(value);
    const size = unitTypes.find((t) => String(t.id) === value)?.numberOfUnits ?? 0;
    // Shrinking (or clearing) the type drops empty slots, never assigned units —
    // otherwise clearing the Select would silently stage every unit for removal.
    setSlots((current) => {
      const filled = current.filter((s) => s.unitId);
      return resize(filled, Math.max(size, filled.length));
    });
  };

  const changeSlot = (index: number, patch: Partial<Slot>) =>
    setSlots((current) => current.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const unitOptionsFor = (index: number) => {
    const takenHere = new Set(slots.filter((_, i) => i !== index).map((s) => s.unitId));
    return assignableUnits
      .filter(
        (u) =>
          !takenHere.has(String(u.unitId)) &&
          (u.assignedPlatformId == null || u.assignedPlatformId === platform.id)
      )
      .map((u) => ({ value: String(u.unitId), label: u.unitNumber }));
  };

  // T values address a physical slot on the platform, so each may be used once.
  const tValueOptionsFor = (index: number) => {
    const takenHere = new Set(
      slots.filter((_, i) => i !== index).map((s) => s.tValue).filter(Boolean)
    );
    return slots
      .map((_, i) => `T${i + 1}`)
      .filter((v) => !takenHere.has(v))
      .map((v) => ({ value: v, label: v }));
  };

  // Only meaningful where T values are actually editable; without that column
  // there would be no way to satisfy the rule.
  const slotsValid = !assignUnits || !tValue || slots.every((s) => !s.unitId || s.tValue);

  const save = () =>
    onSave?.({
      platformUnitTypeId: draftTypeId ? Number(draftTypeId) : null,
      units: assignUnits
        ? slots
            .filter((s) => s.unitId)
            .map((s) => ({
              unitId: Number(s.unitId),
              tValue: tValue ? s.tValue : null,
            }))
        : [],
    });

  // Every destructive or persisting action confirms first, through one dialog.
  type Confirmable = "reset" | "update" | "discard";
  const [confirming, setConfirming] = useState<Confirmable | null>(null);
  const resetPending = pending?.action === "reset";

  useEffect(() => {
    if (!opened) setConfirming(null);
  }, [opened]);

  const confirmAction: Record<Confirmable, () => void> = {
    reset: onReset,
    update: save,
    discard: resetDraft,
  };

  const confirmProcessing: Record<Confirmable, boolean> = {
    reset: resetPending,
    update: savePending,
    discard: false,
  };

  const resetButton = (
    <Button
      variant="default"
      onClick={() => setConfirming("reset")}
      loading={resetPending}
      disabled={anyPending && !resetPending}
    >
      Reset Status
    </Button>
  );

  // One layout for both views: the busy modal renders it read-only, the
  // maintenance modal swaps the controls in. Keeps the two from drifting apart.
  // Platform format (Full/Half) is a VLC concept; LogiCS platforms have only a type.
  const hasPlatformFormat = platform.platformType != null;
  const platformColumns = hasPlatformFormat ? "minmax(0, 1fr) 150px" : "minmax(0, 1fr)";
  const unitColumns = tValue ? "minmax(0, 1fr) 150px" : "minmax(0, 1fr)";
  // VLC addresses a unit by its T value, LogiCS by the side it sits on. One
  // track per flag — the header and rows render a cell for each independently.
  const assignedColumns = `110px ${tValue ? "70px " : ""}${
    platformSide ? "70px " : ""
  }minmax(0, 1fr) 150px`;
  const assignedMinWidth = narrow ? 520 : undefined;

  const platformCard = (
    <Box style={{ border: `1px solid ${c.border}`, borderRadius: 10, overflow: "hidden" }}>
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: platformColumns,
          gap: 16,
          padding: "8px 14px",
          background: c.slate50,
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <Text style={miniLabel}>Platform Type</Text>
        {hasPlatformFormat && <Text style={miniLabel}>Platform</Text>}
      </Box>

      <Box
        style={{
          display: "grid",
          gridTemplateColumns: platformColumns,
          gap: 16,
          alignItems: "center",
          padding: "8px 14px",
        }}
      >
        <Select
          data={unitTypeOptions}
          value={draftTypeId}
          onChange={changeType}
          placeholder={unitTypeOptions.length ? "Select a platform type" : "No platform types for this location"}
          disabled={!editable}
          clearable
          size="sm"
          maxDropdownHeight={220}
          comboboxProps={DROPDOWN_PROPS}
        />

        {hasPlatformFormat && (
          <Text fz={14} c={c.slate500}>
            {platform.platformType}
          </Text>
        )}
      </Box>
    </Box>
  );

  // Only the maintenance view shows platform details; every other busy state is
  // about a unit in motion, where the progress panel is the whole story.
  const editorBlock = (
    <Stack gap="xl" w="100%">
      {!unitsReady && (
        <Alert
          color={assignableUnitsError ? "red" : "gray"}
          variant="light"
          radius="md"
          py="xs"
        >
          {assignableUnitsError
            ? "Could not load the unit list, so editing is disabled. Close and reopen to try again."
            : "Loading units…"}
        </Alert>
      )}

      {platformCard}

      <Box>
        <Box style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <SectionLabel mb={0}>Assigned Units</SectionLabel>
          <Text fz={12} fw={700} c={c.slate400}>
            {slots.length === 0 ? "None" : `${slots.filter((s) => s.unitId).length} of ${slots.length}`}
          </Text>
        </Box>

        {slots.length === 0 ? (
          <Box
            style={{
              border: `1px dashed ${c.border}`,
              borderRadius: 10,
              padding: "18px 14px",
              textAlign: "center",
              color: c.slate400,
              fontSize: 13,
            }}
          >
            Select a platform type to assign units.
          </Box>
        ) : (
          <Box style={{ border: `1px solid ${c.border}`, borderRadius: 10, overflow: "hidden" }}>
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: unitColumns,
                gap: 16,
                padding: "8px 14px",
                background: c.slate50,
                borderBottom: `1px solid ${c.border}`,
              }}
            >
              <Text style={miniLabel}>Unit</Text>
              {tValue && <Text style={miniLabel}>T value</Text>}
            </Box>

            {slots.map((slot, i) => (
              <Box
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: unitColumns,
                  gap: 16,
                  alignItems: "center",
                  padding: "8px 14px",
                  borderBottom: i < slots.length - 1 ? `1px solid ${c.border}` : undefined,
                }}
              >
                <Select
                  data={unitOptionsFor(i)}
                  value={slot.unitId}
                  // Clearing the unit frees its T value; leaving it set would
                  // keep that T value out of every other slot's options.
                  onChange={(value) =>
                    changeSlot(i, value ? { unitId: value } : { unitId: null, tValue: null })
                  }
                  placeholder="Select a unit"
                  disabled={!editable}
                  searchable
                  clearable
                  size="sm"
                  maxDropdownHeight={220}
                  comboboxProps={DROPDOWN_PROPS}
                />

                {tValue && (
                  <Select
                    data={tValueOptionsFor(i)}
                    value={slot.tValue}
                    onChange={(value) => changeSlot(i, { tValue: value })}
                    placeholder="—"
                    disabled={!editable || !slot.unitId}
                    error={!!slot.unitId && !slot.tValue}
                    clearable
                    size="sm"
                    comboboxProps={DROPDOWN_PROPS}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Stack>
  );

  const editorActions = (
    <>
      <Button
        size="xs"
        onClick={() => setConfirming("update")}
        loading={savePending}
        disabled={!dirty || !slotsValid || (anyPending && !savePending)}
      >
        Save Changes
      </Button>
      <Button
        size="xs"
        variant="default"
        onClick={() => setConfirming("discard")}
        disabled={!dirty || anyPending}
      >
        Discard
      </Button>
    </>
  );

  const editor = onSave == null || !assignUnits ? null : editorBlock;

  // Editing is only possible once the platform has physically arrived in the
  // maintenance area, which the backend enforces too.
  const canEdit = phase === "inMaintenance" && editor !== null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      styles={{ title: { flex: 1 } }}
      title={
        <Box style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, width: "100%" }}>
          <Text fw={600} fz="xl" c="primary.6">
            {title}
          </Text>
          {canEdit && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                background: "#ecfdf5",
                color: "#0f766e",
                border: "1px solid #99f6e4",
                fontSize: 11,
                fontWeight: 700,
                padding: "2px 10px",
                borderRadius: 999,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Editable
            </span>
          )}

          {canEdit && (
            <Box style={{ marginLeft: "auto", display: "flex", gap: 8 }}>{editorActions}</Box>
          )}
        </Box>
      }
      centered
      radius="lg"
      size={760}
      padding={narrow ? "md" : undefined}
      xOffset={narrow ? 12 : undefined}
    >
      {errorText && (
        <Alert color="red" radius="md" variant="light" mb="md" withCloseButton onClose={onDismissError}>
          {errorText}
        </Alert>
      )}

      {busy ? (
        <Stack gap="lg">
          {canEdit && editor}

          <Stack
            align="center"
            gap="md"
            p="lg"
            style={{ background: c.slate50, border: `1px solid ${c.border}`, borderRadius: 12 }}
          >
            <Text fw={600} fz="lg" c="primary.6">
              {heading}
            </Text>
            <Text c="dimmed" ta="center" fz="sm">
              {subtext}
            </Text>

          <Stack align="center" gap={6} my="sm">
            {hasActiveUnit && (
              <>
                <Text fz="sm" c="dimmed">
                  Requested Unit
                </Text>
                <Text fw={700} fz="md">
                  Unit {activeUnitNumber}
                </Text>
              </>
            )}
            <PlatformStatusPill platform={platform} />
          </Stack>

          <Box w="70%" pb={28} pt={8}>
            {isMaintenance ? (
              <PlatformStepper
                status={platform.status}
                steps={MAINTENANCE_STEPS}
                activeIndex={maintenanceStepIndex(platform.status)}
              />
            ) : (
              <PlatformStepper status={activeLabel} />
            )}
          </Box>

          {isAtDock && activeUnit && (
            <Text c="green.7" fw={600}>
              Platform has arrived!
            </Text>
          )}

          {!isAtDock && !isMaintenance && (
            <Text c="dimmed" fz="sm">
              Platform is {activeLabel.toLowerCase()}…
            </Text>
          )}

          <Box style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
            {isAtDock && activeUnit && (
              <Button
                color="red"
                radius="md"
                onClick={() => onReturn(activeUnit)}
                loading={pending?.action === "return"}
                disabled={anyPending && pending?.action !== "return"}
              >
                Return Unit to Storage
              </Button>
            )}

            {canReturnFromMaintenance && (
              <Button
                color="teal"
                radius="md"
                onClick={onReturnFromMaintenance}
                loading={pending?.action === "returnFromMaintenance"}
                disabled={anyPending && pending?.action !== "returnFromMaintenance"}
              >
                Return from Maintenance
              </Button>
            )}

            {resetButton}
          </Box>
          </Stack>
        </Stack>
      ) : (
        <Stack gap="lg">
          <Box>
            <Box style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
              <SectionLabel mb={0}>Assigned Units</SectionLabel>
              <Text fz={12} fw={700} c={c.slate400}>
                {platform.units.length === 0 ? "None" : platform.units.length}
              </Text>
            </Box>

            {platform.units.length === 0 ? (
              <Box
                style={{
                  border: `1px dashed ${c.border}`,
                  borderRadius: 10,
                  padding: "18px 14px",
                  textAlign: "center",
                  color: c.slate400,
                  fontSize: 13,
                }}
              >
                No units assigned yet.
              </Box>
            ) : (
              <Box style={{ border: `1px solid ${c.border}`, borderRadius: 10, overflowX: "auto" }}>
                <Box
                  style={{
                    display: "grid",
                    gridTemplateColumns: assignedColumns,
                    minWidth: assignedMinWidth,
                    gap: 16,
                    padding: "8px 14px",
                    background: c.slate50,
                    borderBottom: `1px solid ${c.border}`,
                  }}
                >
                  <Text style={miniLabel}>Unit</Text>
                  {tValue && <Text style={miniLabel}>T value</Text>}
                  {platformSide && <Text style={miniLabel}>Side</Text>}
                  <Text style={miniLabel}>Owner</Text>
                  <span />
                </Box>

                {platform.units.map((unit, i) => (
                  <Box
                    key={unit.unitId}
                    style={{
                      display: "grid",
                      gridTemplateColumns: assignedColumns,
                      minWidth: assignedMinWidth,
                      gap: 16,
                      alignItems: "center",
                      padding: "12px 14px",
                      borderBottom: i < platform.units.length - 1 ? `1px solid ${c.border}` : undefined,
                    }}
                  >
                    <Text fw={600} fz={14} c={c.slate900}>
                      Unit {unit.unitNumber}
                    </Text>
                    {tValue && (
                      <Text fz={13} c={unit.tValue ? c.slate500 : c.slate400}>
                        {unit.tValue ?? "—"}
                      </Text>
                    )}
                    {platformSide && (
                      <Text fz={13} c={unit.platformSide ? c.slate500 : c.slate400}>
                        {unit.platformSide ?? "—"}
                      </Text>
                    )}
                    <Box style={{ minWidth: 0 }}>
                      {unit.owner ? (
                        <>
                          <Text fw={600} fz={13} truncate>
                            {unit.owner.name || "—"}
                          </Text>
                          {unit.owner.email && (
                            <Text fz={12} c="dimmed" truncate>
                              {unit.owner.email}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text fz={13} c="dimmed" fs="italic">
                          Not rented
                        </Text>
                      )}
                    </Box>
                    <Button
                      size="xs"
                      variant="light"
                      fullWidth
                      onClick={() => onRequest(unit)}
                      loading={pending?.action === "request" && pending.unitId === unit.unitId}
                      disabled={anyPending && !(pending?.action === "request" && pending.unitId === unit.unitId)}
                    >
                      Request to Dock
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {canMoveToMaintenance && (
            <Box
              style={{
                display: "flex",
                justifyContent: "flex-end",
                borderTop: "1px solid #e2e8f0",
                paddingTop: 16,
              }}
            >
              <Button
                variant="light"
                color="yellow"
                radius="md"
                onClick={onMoveToMaintenance}
                loading={pending?.action === "moveToMaintenance"}
                disabled={anyPending && pending?.action !== "moveToMaintenance"}
              >
                Request to Maintenance
              </Button>
            </Box>
          )}
        </Stack>
      )}

      <ConfirmationModal
        isOpen={confirming !== null}
        onRequestClose={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming) confirmAction[confirming]();
          setConfirming(null);
        }}
        type={confirming ?? "reset"}
        isProcessing={confirming ? confirmProcessing[confirming] : false}
      />
    </Modal>
  );
}
